/**
 * Employees API — list, get, create, update, delete, uploadPhoto.
 */

import { apiFetch } from './core'

// URLSearchParams stringifies undefined/null as the literal "undefined"/"null",
// which the backend then treats as a real filter value (e.g. status="undefined"
// matches no rows → empty list). Drop empty params before building the query.
const toQuery = (params = {}) => {
    const clean = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    const qs = new URLSearchParams(clean).toString()
    return qs ? `?${qs}` : ''
}

export const employees = {
    list: (params = {}) => apiFetch(`/employees/${toQuery(params)}`),
    myTeam: () => apiFetch('/employees/my-team'),
    /** Коллеги — список выбора, открытый рядовому сотруднику.
     *  `list()` ему отдаёт пустоту: team scope сужает выборку до подчинённых,
     *  а их у него нет. Эта ручка отдаёт коллег его отдела (руководителю —
     *  его отделы, admin/hr/owner/ceo — организацию целиком). */
    colleagues: () => apiFetch('/employees/colleagues'),
    /** Подрядчики моего отдела — узкий список для приёмки работы (без коммерции договора).
     *  Без префикса `/employees`: роутер employees подключён в router.py БЕЗ prefix,
     *  и маршрут живёт по адресу `/departments/my/contractors`. */
    myDepartmentContractors: () => apiFetch('/departments/my/contractors'),
    /** Active employees of the org, ignoring team scope — for equipment-assignment dropdowns. */
    forAssetAssignment: () => apiFetch('/employees/for-asset-assignment'),
    /** Canonical + actual employee positions list — feeds RoleCombobox entity picker. */
    positions: () => apiFetch('/employees/positions'),
    /** Employees eligible to be line manager — filtered by managerial positions/roles/hierarchy. */
    managerCandidates: (params = {}) => apiFetch(`/employees/manager-candidates${toQuery(params)}`),
    get: (id) => apiFetch(`/employees/${id}`),
    create: (data) => apiFetch('/employees', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    update: (id, data) => apiFetch(`/employees/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),
    delete: (id) => apiFetch(`/employees/${id}`, { method: 'DELETE' }),

    /** История версий платёжных реквизитов (доступна сотруднику по себе). */
    requisitesHistory: (id) => apiFetch(`/employees/${id}/payment-requisites/history`),
    /**
     * Сотрудник подтверждает свои реквизиты (или admin/HR за него).
     * `itemKey` — конкретный способ выплаты (карта, тайский счёт, USDT);
     * без него подтверждаются все заполненные способы разом.
     */
    confirmRequisites: (id, itemKey = null) => apiFetch(`/employees/${id}/payment-requisites/confirm`, {
        method: 'POST',
        body: JSON.stringify({ item_key: itemKey }),
    }),
    // Совмещение должностей (concurrent positions) — сотрудник на нескольких
    // позициях одновременно. Отдельно от .positions() (каталог должностей).
    concurrentPositions: (id) => apiFetch(`/employees/${id}/positions`),
    addConcurrentPosition: (id, data) => apiFetch(`/employees/${id}/positions`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    updateConcurrentPosition: (id, positionId, data) =>
        apiFetch(`/employees/${id}/positions/${positionId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }),
    /** Переносит должность из карточки сотрудника в список как основную. */
    adoptPrimaryPosition: (id) =>
        apiFetch(`/employees/${id}/positions/adopt-primary`, { method: 'POST' }),
    removeConcurrentPosition: (id, positionId) =>
        apiFetch(`/employees/${id}/positions/${positionId}`, { method: 'DELETE' }),

    // PS2 login mapping (несколько логинов на сотрудника)
    crmLogins: (id) => apiFetch(`/employees/${id}/crm-logins`),
    addCrmLogin: (id, data) => apiFetch(`/employees/${id}/crm-logins`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    deleteCrmLogin: (id, loginId) => apiFetch(`/employees/${id}/crm-logins/${loginId}`, { method: 'DELETE' }),

    /** Upload photo file — multipart/form-data POST */
    uploadPhoto: async (id, file) => {
        const formData = new FormData()
        formData.append('file', file)
        return apiFetch(`/employees/${id}/photo`, { method: 'POST', body: formData })
    },

    /** Merge duplicate employee into primary */
    merge: (primaryId, duplicateId) => apiFetch(`/employees/${primaryId}/merge/${duplicateId}`, {
        method: 'POST',
    }),

    /**
     * Поглотить дубль без статуса «уволен»: перенос полей + привязка кандидата + удаление второй записи.
     * Только если у дубля нет учётной записи User (иначе 409). Перед вызовом
     * можно дёрнуть `users.detachEmployee(account_user_id)` чтобы отвязать
     * учётку дубля.
     *
     * query.override_fields — массив имён полей (experience, education, …),
     * которые перетереть данными из дубля даже если у canonical непусто.
     */
    consolidateDuplicate: (canonicalId, duplicateId, query = {}) => {
        const qs = new URLSearchParams()
        if (query.candidate_id) qs.set('candidate_id', query.candidate_id)
        if (query.force) qs.set('force', 'true')
        if (Array.isArray(query.override_fields) && query.override_fields.length) {
            qs.set('override_fields', query.override_fields.join(','))
        }
        const q = qs.toString()
        return apiFetch(`/employees/${canonicalId}/consolidate-duplicate/${duplicateId}${q ? `?${q}` : ''}`, {
            method: 'POST',
        })
    },
}
